import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { randomId } from 'server/shared/helpers/random';
import { Certificate } from 'server/shared/models/certificate';
import { CertificateGroup } from 'server/shared/models/certificateGroup';
import { Customer } from 'server/shared/models/customer';
import { MappedCertificate } from 'server/shared/models/user';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
type CertificateGroupInCreation = {
  displayName: string;
  certificates: MappedCertificate[];
};

@Component({
  selector: 'admin-certificate-groups',
  templateUrl: 'certificate-groups.component.html',
  styleUrls: ['certificate-groups.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CertificateGroupsComponent implements OnDestroy {
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);

  private destroyed$ = new Subject<void>();

  certificateGroups$ = new BehaviorSubject<CertificateGroup[]>([]);
  initialCertificateGroups: CertificateGroup[] = [];
  certificateGroupsInCreation$ = new BehaviorSubject<
    CertificateGroupInCreation[]
  >([]);

  constructor(
    private location: Location,
    private toastService: ToastService,
    private customerService: CustomerService,
    private permissionService: PermissionsService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.customer$.next(customer);
        this.initializeCertificateGroups();
      });
    this.permissionService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });
  }

  initializeCertificateGroups() {
    if (!this.customer$.value) return;

    let certificateGroupFromCustomer =
      this.customer$.value.certificateGroups || [];

    if (this.certificateGroups$.value.length === 0) {
      this.certificateGroups$.next([...certificateGroupFromCustomer]);
    } else {
      let currentReasons = [...this.certificateGroups$.value];
      certificateGroupFromCustomer.forEach(
        (certificateGroupFromCustomer: CertificateGroup) => {
          let certificateGroupIndex = currentReasons.findIndex(
            (certificateGroup) => {
              return certificateGroup.id === certificateGroupFromCustomer.id;
            },
          );
          if (certificateGroupIndex === -1) {
            currentReasons.push(certificateGroupFromCustomer);
          }
          if (
            this.initialCertificateGroups[certificateGroupIndex] &&
            currentReasons[certificateGroupIndex].displayName ===
              this.initialCertificateGroups[certificateGroupIndex].displayName
          ) {
            currentReasons[certificateGroupIndex] =
              certificateGroupFromCustomer;
          }
        },
      );
      this.certificateGroups$.next(currentReasons);
    }

    this.initialCertificateGroups = [
      ...JSON.parse(JSON.stringify(certificateGroupFromCustomer)),
    ];
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  historyBack() {
    this.location.back();
  }

  addNewCertificateGroup() {
    const current = [...this.certificateGroupsInCreation$.value];
    current.push({
      displayName: '',
      certificates: [],
    });
    this.certificateGroupsInCreation$.next(current);
  }

  cancelNewCertificateGroup(index: number) {
    const current = [...this.certificateGroupsInCreation$.value];
    current.splice(index, 1);
    this.certificateGroupsInCreation$.next(current);
  }

  saveNewCertificateGroup(index: number) {
    let displayName =
      this.certificateGroupsInCreation$.value[index]?.displayName;
    let certificates =
      this.certificateGroupsInCreation$.value[index]?.certificates;
    if (!displayName || !this.customer$.value) return;
    let newCertificateGroup: CertificateGroup = {
      id: randomId(),
      displayName: displayName,
      certificates: certificates,
    };

    const currentReasons = [...this.certificateGroups$.value];
    currentReasons.push(newCertificateGroup);
    this.certificateGroups$.next(currentReasons);

    const currentInCreation = [...this.certificateGroupsInCreation$.value];
    currentInCreation.splice(index, 1);
    this.certificateGroupsInCreation$.next(currentInCreation);

    Meteor.call(
      'addCertificateGroup',
      this.customer$.value._id,
      newCertificateGroup,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  deleteCertificateGroup(certificateGroupId: string) {
    if (!this.customer$.value || !certificateGroupId) return;
    const currentReasons = [...this.certificateGroups$.value];
    let index = currentReasons.findIndex((certificateGroup) => {
      return certificateGroup.id === certificateGroupId;
    });
    currentReasons.splice(index, 1);
    this.certificateGroups$.next(currentReasons);

    Meteor.call(
      'removeCertificateGroup',
      this.customer$.value._id,
      certificateGroupId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  cancelChanges(certificateGroupId: string) {
    let index = this.initialCertificateGroups.findIndex((certificateGroup) => {
      return certificateGroup.id === certificateGroupId;
    });

    const currentReasons = [...this.certificateGroups$.value];
    currentReasons[index].displayName =
      this.initialCertificateGroups[index].displayName;
    currentReasons[index].certificates =
      this.initialCertificateGroups[index].certificates;
    this.certificateGroups$.next(currentReasons);
  }

  saveCertificateGroup(certificateGroupId: string): void {
    let certificateGroup = this.certificateGroups$.value.find(
      (certificateGroup) => {
        return certificateGroup.id === certificateGroupId;
      },
    );
    if (!this.customer$.value || !certificateGroup) return;
    Meteor.call(
      'updateCertificateGroup',
      this.customer$.value._id,
      certificateGroup,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  certificatesSelected(
    certificateGroup: CertificateGroup | CertificateGroupInCreation,
    certificates: Certificate[],
  ) {
    certificateGroup.certificates = certificates.map((certificate) => {
      return {
        id: certificate._id,
        displayName: certificate.name,
      };
    });
  }

  trackById(index: number, object: CertificateGroup): string {
    return object.id;
  }
}
